import {
  Alert,
  Button,
  Center,
  Divider,
  Group,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconRefresh } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import {
  isPermissionGranted,
  isPuiVersionEnough,
  isVersionUSBUpdateNeeded,
} from "@utils/CommonUtil";

import { openConfirmation } from "@actions/actions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DEVICE_PERMISSION_LABEL_KEYS } from "@utils/devicePermissions";
import { VERSION_UPDATE_TUTORIAL_DOC_URL } from "@constants/externalTutorialLinks";

interface Props {
  product: any;
  permissions?: Record<string, unknown> | null;
  version: string;
  versionData: any;
  availableVersions: string[];
  osUpdateRequired: boolean;
  appUpdateRequired: boolean;
  canShowUpdateButton: boolean;
  deviceStatus: number;
  // New: a component (node) provided by parent to render device status badge/controls
  deviceStatusComponent?: React.ReactNode;
  submitUpdate: (cb: () => void) => void;
  classes?: { productTable?: string } | any;
  hideUsbUpdateModal: boolean;
}

const DeviceInfoTable: React.FC<Props> = ({
  product,
  permissions,
  version,
  versionData,
  availableVersions,
  osUpdateRequired,
  appUpdateRequired,
  canShowUpdateButton,
  deviceStatus,
  deviceStatusComponent,
  submitUpdate,
  classes,
  hideUsbUpdateModal,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [usbModalShown, setUsbModalShown] = useState(hideUsbUpdateModal);
  const usbUpdateNeeded = isVersionUSBUpdateNeeded(product.version);

  // If an app update is available and the device version requires USB update, show modal (once)
  useEffect(() => {
    if (appUpdateRequired && usbUpdateNeeded && !usbModalShown) {
      redirectUsbUpdate();
      setUsbModalShown(true);
    }
  }, [appUpdateRequired]);

  const redirectUsbUpdate = () => {
    try {
      dispatch(
        openConfirmation(
          t("VersionControl.usbRequiredTitle") || "Cable update required",
          t("VersionControl.usbRequiredMessage") ||
            "This device's version is too old and must be updated using a USB cable. Do you want to go to the USB update page?",
          () => navigate("/usb-version-control"),
          true,
          t("close"),
          t("VersionControl.goToUsb") || "Go to USB update",
        ),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const updateInfo = osUpdateRequired
    ? "osUpdateInfo"
    : appUpdateRequired
      ? "appUpdateInfo"
      : "noUpdateInfo";

  const handleUpdateClick = () => {
    // Directly start update flow; USB modal is shown automatically when applicable
    submitUpdate(() => {
      // parent will supply the callback that advances UI state (setUpdateMode, setActiveStepNo, timeline)
    });
  };

  function getVersionInfoWarnings() {
    if (usbUpdateNeeded && !usbModalShown) {
      return (
        <Alert
          onClick={() => navigate("/usb-version-control")}
          title={t("VersionControl.usbRequiredTitle")}
          color="red"
          variant="outline"
        >
          {t("VersionControl.usbRequiredMessage")}
        </Alert>
      );
    }
    if (canShowUpdateButton) {
      return (
        <>
          <Divider m={10} />
          {deviceStatus < 0 ? (
            <Alert title={t("deviceNotConnected")} color="red" variant="outline">
              {t("VersionControl.deviceNotConnectedText")}
            </Alert>
          ) : process.env.REACT_APP_OFFLINE === "true" ? (
            <Alert title={t("Warning")} color="red" variant="outline">
              {t("VersionControl.useOnlineVersion")}
            </Alert>
          ) : (
            <Center>
              <Button
                leftSection={<IconRefresh />}
                variant="filled"
                color="orange"
                disabled={deviceStatus < 0}
                onClick={() => handleUpdateClick()}
              >
                {t("VersionControl.update")}
              </Button>
            </Center>
          )}
        </>
      );
    }
  }

  return (
    <Table className={classes?.productTable} withColumnBorders withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <th colSpan={2}>
            <Title order={3} style={{ textAlign: "center" }}>
              {t("VersionControl.productInfo")}
            </Title>
          </th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {/* Status row - parent provides the status component (e.g., DeviceStatusBadge) */}
        <Table.Tr key={product.pid + "status"}>
          <Table.Td>{t("VersionControl.status")}</Table.Td>
          <Table.Td>
            <Center>
              {deviceStatusComponent ? deviceStatusComponent : <Text ta="center">-</Text>}
            </Center>
          </Table.Td>
        </Table.Tr>
        <Table.Tr key={product.pid}>
          <Table.Td>ID</Table.Td>
          <Table.Td>
            <Text ta="center" fz="sm" c="dimmed" fw={600}>
              {product.uuid}
              {product.alias ? " (" + product.alias + ")" : null}
            </Text>
          </Table.Td>
        </Table.Tr>

        {permissions && (
          <Table.Tr key={product.pid + "permissions"}>
            <Table.Td>{t("OrganizationSettings.permissions")}</Table.Td>
            <Table.Td>
              <Stack gap={4} align="center">
                {Object.entries(permissions).map(([key, value]) => {
                  const granted = isPermissionGranted(value);
                  return (
                    <Group key={key} gap="xs" wrap="nowrap">
                      <ThemeIcon color={granted ? "green" : "red"} size="sm" variant="light">
                        {granted ? <IconCheck /> : <IconAlertCircle />}
                      </ThemeIcon>
                      <Text fz="sm">
                        {DEVICE_PERMISSION_LABEL_KEYS[key]
                          ? t(DEVICE_PERMISSION_LABEL_KEYS[key])
                          : key}
                        {granted ? "" : " — " + t("OrganizationSettings.permissionMissing")}
                      </Text>
                    </Group>
                  );
                })}
              </Stack>
            </Table.Td>
          </Table.Tr>
        )}

        <Table.Tr key={product.pid + "version"}>
          <Table.Td>{t("VersionControl.version")}</Table.Td>
          <Table.Td>
            <Text ta="center" fz="sm" c="dimmed" fw={600}>
              {version}
            </Text>
          </Table.Td>
        </Table.Tr>

        <Table.Tr key={product.pid + "pui"}>
          <Table.Td>{t("VersionControl.puiVersion")}</Table.Td>
          <Table.Td>
            <Text ta="center" fz="sm" c="dimmed" fw={600}>
              {product.puiVersion}
            </Text>
            {isPuiVersionEnough(product.puiVersion) ? null : (
              <a href="/versions">
                <Text variant="filled" color="red" ta="center" fz="md" fw={700} td={"underline"}>
                  {t("VersionControl.osUpdateInfo")}
                </Text>
              </a>
            )}
          </Table.Td>
        </Table.Tr>

        <Table.Tr key={product.pid + "latestPui"}>
          <Table.Td>{t("VersionControl.latestVersion")}</Table.Td>
          <Table.Td>
            {versionData?.latestApkVersion ? (
              <a href={`/versions/${versionData.latestApkVersion}`} target="_blank">
                <Text ta="center" fz="sm" fw={600} td="underline">
                  {versionData.latestApkVersion}
                </Text>
              </a>
            ) : (
              <Text ta="center" fz="sm" c="dimmed" fw={600}>
                -
              </Text>
            )}
            {versionData?.description ? (
              <Text ta="center" fz="sm" c="dimmed">
                {versionData.description}
              </Text>
            ) : versionData?.features && versionData.features?.length > 0 ? (
              <Text ta="center" fz="sm" c="dimmed">
                {versionData.features.join(", ")}
              </Text>
            ) : null}
          </Table.Td>
        </Table.Tr>

        <Table.Tr key={"availableVersions"}>
          <Table.Td>{t("VersionControl.availableVersions")}</Table.Td>
          <Table.Td>
            <Text ta="center" fz="sm" c="dimmed" fw={600}>
              {availableVersions ? availableVersions.join(", ") : "-"}
            </Text>
          </Table.Td>
        </Table.Tr>

        <Table.Tr key={product.pid + "info"}>
          <Table.Td>{t("VersionControl.versionInfo")}</Table.Td>
          <Table.Td>
            <Text variant="filled" color="red" ta="center" fz="md" fw={700}>
              {t("VersionControl." + updateInfo)}
            </Text>
            {updateInfo === "appUpdateInfo" && (
              <Text ta="center" fz="md">
                {t("VersionControl." + updateInfo + "Text")}
              </Text>
            )}

            <a href={VERSION_UPDATE_TUTORIAL_DOC_URL} target="_blank" rel="noopener noreferrer">
              <Text variant="filled" color="blue" ta="center" fz="md" fw={700} td={"underline"}>
                {t("Instructions")}
              </Text>
            </a>

            {getVersionInfoWarnings()}
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );
};

export default DeviceInfoTable;
